// src/components/Chat.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Bot as BotIconLucide, Send, MessageSquarePlus, Loader2,
    MessageSquareText, AlertTriangle, Menu as MenuIcon, List // Dodano List dla ikony listy konwersacji
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { PageContentWrapper } from '../main/LayoutStyles';
import {
    ChatAppWrapper,
    ConversationsSidebar,
    ConversationList,
    ConversationListItem,
    NewChatButton,
    ChatArea,
    ChatHeaderStyled,
    // MobileChatMenuButton, // Zastąpiony przez ChatSidebarToggleButton użyty w ChatHeaderActions
    ChatHeaderActions,      // Nowy import
    ChatSidebarToggleButton,// Nowy import
    ConversationTitle,
    MessagesList,
    MessageItem,
    MessageBubble,
    TypingIndicatorBubble,
    ChatInputArea,
    ChatTextarea,
    SendButton,
    NoConversationsMessage,
    MobileConversationsOverlay
} from './ChatStyles';
import apiClient from '../services/api';
import { useAuth } from '../auth/AuthContext';

const BotChatIcon = () => <BotIconLucide size={28} />;

const getMarkdownString = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) { console.warn("Markdown otrzymał tablicę:", value); return value.map(String).join('\n'); }
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') { console.error("Markdown otrzymał obiekt:", value); return '[Błąd: Nieprawidłowy format treści]'; }
    return String(value);
};

const ChatPage = () => {
    const { currentUser, ghostUserId } = useAuth();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [error, setError] = useState(null);
    const [chatError, setChatError] = useState(null);

    const [conversations, setConversations] = useState([]);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [initialBotWelcomeDone, setInitialBotWelcomeDone] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const chatAreaRef = useRef(null);
    const chatInputAreaRef = useRef(null);
    const justCreatedConversationIdRef = useRef(null);
    const initialLoadAttemptedRef = useRef(false); // Do jednorazowego przywrócenia czatu

    const getStorageKey = useCallback(() => {
        if (currentUser) return `LAST_ACTIVE_CHAT_ID_USER_${currentUser.id}`;
        if (ghostUserId) return `LAST_ACTIVE_CHAT_ID_GUEST_${ghostUserId}`;
        return null;
    }, [currentUser, ghostUserId]);

    useEffect(() => {
        const key = getStorageKey();
        if (key && currentConversationId) {
            localStorage.setItem(key, currentConversationId);
        } else if (key && !currentConversationId) {
            // Nie usuwaj klucza, gdy currentConversationId jest null, bo chcemy go przywrócić.
            // Usuwamy tylko przy "handleNewConversation" lub wylogowaniu (co powinno być obsłużone w AuthContext)
        }
    }, [currentConversationId, getStorageKey]);

    // Reset flagi próby załadowania, gdy zmienia się użytkownik/gość
    useEffect(() => {
        initialLoadAttemptedRef.current = false;
        // Potencjalnie, jeśli currentConversationId nie pasuje do nowego użytkownika, można je wyzerować
        // Ale to może być skomplikowane, jeśli ID są globalne.
        // Na razie, fetchConversations i logika poniżej powinny sobie poradzić.
    }, [currentUser, ghostUserId]);


    // Efekt do przywracania ostatnio aktywnego czatu
    useEffect(() => {
        const key = getStorageKey();
        // Sprawdź, czy konwersacje są załadowane, nie ma aktywnego czatu i nie próbowaliśmy jeszcze tego robić
        if (key && !isLoadingConversations && conversations.length > 0 && !currentConversationId && !initialLoadAttemptedRef.current) {
            initialLoadAttemptedRef.current = true; // Zaznacz, że podjęto próbę
            const lastActiveId = localStorage.getItem(key);
            if (lastActiveId && conversations.find(c => c.id === lastActiveId)) {
                console.log("Przywracanie ostatniego aktywnego czatu:", lastActiveId);
                setCurrentConversationId(lastActiveId); // To wywoła useEffect do pobrania wiadomości
            }
        }
    }, [conversations, isLoadingConversations, currentConversationId, getStorageKey]);


    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        if (!isBotTyping && !isLoadingMessages && !isLoadingConversations && textareaRef.current) {
            const timer = setTimeout(() => {
                if (textareaRef.current) textareaRef.current.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isBotTyping, isLoadingMessages, isLoadingConversations, currentConversationId]);

    const formatConversationTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const timeString = date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
        if (date.toDateString() === today.toDateString()) return `dziś, ${timeString}`;
        if (date.toDateString() === yesterday.toDateString()) return `wczoraj, ${timeString}`;
        return `${date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${timeString}`;
    };

    const fetchConversations = useCallback(async () => {
        if (!currentUser && !ghostUserId) {
            setConversations([]);
            setIsLoadingConversations(false);
            return;
        }
        setIsLoadingConversations(true);
        setError(null);
        try {
            const response = await apiClient.get('/chat/conversations');
            const sortedConversations = Array.isArray(response.data)
                ? response.data.sort((a, b) => new Date(b.lastUpdatedAt) - new Date(a.lastUpdatedAt))
                : [];
            setConversations(sortedConversations);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || "Nie udało się pobrać listy konwersacji.";
            setError(errorMsg);
            setConversations([]);
        } finally {
            setIsLoadingConversations(false);
        }
    }, [currentUser, ghostUserId]);

    const fetchMessages = useCallback(async (conversationId) => {
        if (!conversationId) {
            setIsLoadingMessages(false);
            return;
        }
        setIsLoadingMessages(true);
        setChatError(null);
        try {
            const response = await apiClient.get(`/chat/history/${conversationId}`);
            const fetchedMessages = (response.data && Array.isArray(response.data.messages))
                ? response.data.messages.map(msg => ({ ...msg, sender: String(msg.senderType).toUpperCase() === 'USER' ? 'user' : 'bot', isError: false }))
                : [];
            setMessages(fetchedMessages);
            setInitialBotWelcomeDone(true);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || "Nie udało się pobrać wiadomości.";
            setChatError(errorMsg);
            setMessages([]);
        } finally {
            setIsLoadingMessages(false);
        }
    }, []);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    useEffect(() => {
        if (currentConversationId) {
            if (justCreatedConversationIdRef.current === currentConversationId) {
                setInitialBotWelcomeDone(true);
                justCreatedConversationIdRef.current = null;
            } else {
                fetchMessages(currentConversationId);
            }
        } else {
            if (!initialBotWelcomeDone) {
                setMessages([{ id: 'initial-bot-msg-dynamic', content: "Witaj! Jestem Twoim asystentem AI. W czym mogę dzisiaj pomóc?", sender: "bot", timestamp: new Date().toISOString() }]);
                setInitialBotWelcomeDone(true);
            }
        }
    }, [currentConversationId, fetchMessages, initialBotWelcomeDone]);


    const handleSelectConversation = (id) => {
        if (isLoadingMessages || currentConversationId === id) return;
        justCreatedConversationIdRef.current = null;
        setCurrentConversationId(id);
        if (isMobileSidebarOpen) setIsMobileSidebarOpen(false);
    };

    const handleNewConversation = () => {
        if (isLoadingMessages || isLoadingConversations) return;
        justCreatedConversationIdRef.current = null;
        setCurrentConversationId(null);
        setInitialBotWelcomeDone(false);
        setError(null);
        setChatError(null);
        const key = getStorageKey(); // Usuń zapisany ostatni aktywny czat
        if (key) localStorage.removeItem(key);

        if (textareaRef.current) {
            textareaRef.current.value = "";
            setInputValue("");
            textareaRef.current.focus();
        }
        if (isMobileSidebarOpen) setIsMobileSidebarOpen(false);
    };

    const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);

    const handleSendMessage = async () => {
        if (inputValue.trim() === "" || isBotTyping || isLoadingMessages) return;
        const userMessageText = inputValue;
        const tempUserMessageId = `user-${Date.now()}`;
        const wasNewConversationFlow = !currentConversationId;

        setMessages(prev => [...prev, { id: tempUserMessageId, content: userMessageText, sender: "user", timestamp: new Date().toISOString() }]);
        setInputValue("");
        setIsBotTyping(true);
        setChatError(null);

        const requestBody = {
            message: userMessageText,
            ...(currentConversationId && { conversationId: currentConversationId })
        };

        try {
            const response = await apiClient.post('/chat/send', requestBody);
            const data = response.data;

            const aiMsg = {
                id: data.aiMessageId || `ai-${Date.now()}`,
                content: data.content,
                sender: "bot",
                timestamp: data.timestamp || new Date().toISOString()
            };

            setMessages(prev =>
                prev.map(msg =>
                    msg.id === tempUserMessageId ? { ...msg, id: data.userMessageId || tempUserMessageId } : msg
                ).concat([aiMsg])
            );

            if (data.conversationId) {
                if (wasNewConversationFlow) {
                    justCreatedConversationIdRef.current = data.conversationId;
                }
                setCurrentConversationId(data.conversationId);
                fetchConversations();
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || "Nie udało się wysłać wiadomości.";
            setChatError(errorMsg);
            setMessages(prev => [...prev, { id: `error-${Date.now()}`, content: `Błąd: ${errorMsg}`, sender: "bot", timestamp: new Date().toISOString(), isError: true }]);
        } finally {
            setIsBotTyping(false);
        }
    };

    const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey && !isBotTyping && !isLoadingMessages) { e.preventDefault(); handleSendMessage(); }};

    useEffect(() => {
        const visualViewport = window.visualViewport;
        if (!visualViewport) return;

        const currentChatAreaNode = chatAreaRef.current;
        const currentTextareaNode = textareaRef.current;
        const currentChatInputAreaNode = chatInputAreaRef.current;

        const adjustChatAreaHeight = () => {
            if (currentChatAreaNode) {
                const availableHeight = visualViewport.height;
                currentChatAreaNode.style.height = `${availableHeight}px`;
                if (document.activeElement === currentTextareaNode && currentChatInputAreaNode) {
                    setTimeout(() => currentChatInputAreaNode.scrollIntoView({ behavior: 'smooth', block: 'end' }), 100);
                } else {
                    setTimeout(scrollToBottom, 100);
                }
            }
        };
        adjustChatAreaHeight();
        visualViewport.addEventListener('resize', adjustChatAreaHeight);
        visualViewport.addEventListener('scroll', adjustChatAreaHeight);
        return () => {
            visualViewport.removeEventListener('resize', adjustChatAreaHeight);
            visualViewport.removeEventListener('scroll', adjustChatAreaHeight);
            if (currentChatAreaNode) {
                currentChatAreaNode.style.height = '100%';
            }
        };
    }, [scrollToBottom]);

    const handleTextareaFocus = () => {
        setTimeout(() => {
            if (chatInputAreaRef.current) {
                chatInputAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        }, 300);
    };

    const chatDisplayTitle = "Asystent AI";
    // Warunek wyświetlania ikony "Nowy Czat"
    const showNewChatIcon = currentConversationId &&
        (messages.length > 1 || (messages.length === 1 && messages[0]?.id !== 'initial-bot-msg-dynamic'));


    return (
        <PageContentWrapper className="no-padding-or-scroll-managed-internally" style={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
            <ChatAppWrapper>
                <MobileConversationsOverlay className={isMobileSidebarOpen ? 'open' : ''} onClick={toggleMobileSidebar} />
                <ConversationsSidebar isOpen={isMobileSidebarOpen} className={isMobileSidebarOpen ? 'open' : ''}>
                    <NewChatButton onClick={handleNewConversation} disabled={isLoadingMessages || isLoadingConversations}>
                        <MessageSquarePlus size={18} /> Nowy Czat {/* Ten przycisk jest zawsze w sidebarze */}
                    </NewChatButton>
                    {/* ... reszta sidebar ... */}
                    {isLoadingConversations && <div style={{ textAlign: 'center', padding: '20px' }}><Loader2 className="icon-spin"/> Ładowanie...</div>}
                    {!isLoadingConversations && conversations.length > 0 && (
                        <ConversationList className="custom-scrollbar">
                            {conversations.map(conv => (
                                <ConversationListItem key={conv.id} onClick={() => handleSelectConversation(conv.id)} className={currentConversationId === conv.id ? 'active' : ''} title={conv.title || `Rozmowa ${conv.id}`}>
                                    <div className="conv-title">{conv.title || `Rozmowa`}</div>
                                    <div className="conv-snippet">{conv.firstMessageSnippet || 'Brak podglądu...'}</div>
                                    <div className="conv-timestamp">{formatConversationTimestamp(conv.lastUpdatedAt)}</div>
                                </ConversationListItem>
                            ))}
                        </ConversationList>
                    )}
                    {!isLoadingConversations && conversations.length === 0 && !error && <NoConversationsMessage><MessageSquareText size={48}/>Brak rozmów.</NoConversationsMessage>}
                    {!isLoadingConversations && error && <div style={{color: 'red', padding: '20px', textAlign: 'center'}}><AlertTriangle/> {error}</div>}
                </ConversationsSidebar>

                <ChatArea ref={chatAreaRef}>
                    <ChatHeaderStyled>
                        {/* Pusty div dla wyrównania, jeśli główny przycisk menu aplikacji byłby tutaj */}
                        <div style={{ width: '44px', flexShrink: 0 }} />

                        <ConversationTitle>{chatDisplayTitle}</ConversationTitle>

                        <ChatHeaderActions className="desktop-chat-actions"> {/* Klasa do sterowania widocznością CSS */}
                            {showNewChatIcon && (
                                <ChatSidebarToggleButton onClick={handleNewConversation} title="Nowy czat">
                                    <MessageSquarePlus size={22} />
                                </ChatSidebarToggleButton>
                            )}
                            <ChatSidebarToggleButton onClick={toggleMobileSidebar} title="Pokaż/Ukryj listę konwersacji">
                                <List size={24} />
                            </ChatSidebarToggleButton>
                        </ChatHeaderActions>
                    </ChatHeaderStyled>

                    {/* ... reszta ChatArea ... */}
                    {isLoadingMessages ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Loader2 className="icon-spin" size={32} /> Ładowanie...</div>
                    ) : (
                        <MessagesList className="custom-scrollbar">
                            {messages.map((msg) => (
                                <MessageItem key={msg.id} sender={msg.sender}>
                                    {msg.sender === 'bot' && msg.isError && <BotChatIcon />}
                                    <MessageBubble sender={msg.sender} style={msg.isError ? { backgroundColor: '#fee2e2', color: '#c62828', borderColor: '#ef9a9a' } : {}}>
                                        {msg.sender === 'bot' && !msg.isError ? (
                                            <ReactMarkdown components={{
                                                p: ({node, ...props}) => <p style={{marginBottom: '0.5em', marginTop:0}} {...props} /> ,
                                            }}>
                                                {getMarkdownString(msg.content)}
                                            </ReactMarkdown>
                                        ) : ( <p style={{margin:0}}>{getMarkdownString(msg.content)}</p> )}
                                    </MessageBubble>
                                </MessageItem>
                            ))}
                            {isBotTyping && (
                                <MessageItem sender="bot">
                                    <TypingIndicatorBubble sender="bot"><span></span><span></span><span></span></TypingIndicatorBubble>
                                </MessageItem>
                            )}
                            <div ref={messagesEndRef} />
                        </MessagesList>
                    )}
                    {chatError && messages.length === 0 && !isLoadingMessages && currentConversationId && (
                        <div style={{color: 'red', padding: '20px', textAlign: 'center', margin: 'auto'}}><AlertTriangle/> {chatError}</div>
                    )}
                    <ChatInputArea ref={chatInputAreaRef}>
                        <ChatTextarea
                            ref={textareaRef}
                            rows="1"
                            className="custom-scrollbar"
                            placeholder="Napisz wiadomość..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isBotTyping || isLoadingMessages || isLoadingConversations}
                            onFocus={handleTextareaFocus}
                        />
                        <SendButton onClick={handleSendMessage} disabled={isBotTyping || isLoadingMessages || isLoadingConversations || inputValue.trim() === ""} aria-label="Wyślij wiadomość">
                            <Send size={18} />
                        </SendButton>
                    </ChatInputArea>
                </ChatArea>
            </ChatAppWrapper>
        </PageContentWrapper>
    );
};

export default ChatPage;